import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  FolderOpen,
  Settings,
  BookOpen,
  MessageCircleQuestion,
  MessageCircle,
  Menu,
  Plus,
  LogOut,
  LogIn,
  User,
  Globe,
  FlaskConical,
} from 'lucide-react';
import { SearchBar } from '@/components/SearchBar';
import { useSearch } from '@/contexts/SearchContext';
import { openTaskForm } from '@/lib/openTaskForm';
import { useProject } from '@/contexts/ProjectContext';
import { useOpenProjectInEditor } from '@/hooks/useOpenProjectInEditor';
import { useOpenProjectInVscodeWeb } from '@/hooks/useOpenProjectInVscodeWeb';
import { OpenInIdeButton } from '@/components/ide/OpenInIdeButton';
import { useProjectRepos } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { OAuthDialog } from '@/components/dialogs/global/OAuthDialog';
import { useUserSystem } from '@/components/ConfigProvider';
import { oauthApi, localAuthApi } from '@/lib/api';
import { usePermissions } from '@/hooks/usePermissions';

const INTERNAL_NAV = [{ label: 'Projects', icon: FolderOpen, to: '/projects' }];

const EXTERNAL_LINKS = [
  {
    label: 'Docs',
    icon: BookOpen,
    href: import.meta.env.VITE_DOCS_URL,
  },
  {
    label: 'Support',
    icon: MessageCircleQuestion,
    href: import.meta.env.VITE_SUPPORT_URL,
  },
  {
    label: 'Discord',
    icon: MessageCircle,
    href: import.meta.env.VITE_DISCORD_URL,
  },
].filter((link) => Boolean(link.href));

function NavDivider() {
  return (
    <div
      className="mx-2 h-6 w-px bg-border/60"
      role="separator"
      aria-orientation="vertical"
    />
  );
}

export function Navbar() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId, project } = useProject();
  const { query, setQuery, active, clear, registerInputRef } = useSearch();
  const handleOpenInEditor = useOpenProjectInEditor(project || null);
  const handleOpenInVscodeWeb = useOpenProjectInVscodeWeb(project || null);
  const { loginStatus, reloadSystem, config, updateAndSaveConfig } =
    useUserSystem();

  const { data: repos } = useProjectRepos(projectId);
  const isSingleRepoProject = repos?.length === 1;

  const setSearchBarRef = useCallback(
    (node: HTMLInputElement | null) => {
      registerInputRef(node);
    },
    [registerInputRef]
  );
  const { t } = useTranslation(['tasks', 'common']);
  const { hasPermission, isAdmin } = usePermissions();
  // Navbar is global, but the share tasks toggle only makes sense on the tasks route
  const isTasksRoute = /^\/projects\/[^/]+\/tasks/.test(location.pathname);
  const showSharedTasks = searchParams.get('shared') !== 'off';
  const shouldShowSharedToggle =
    isTasksRoute && active && project?.remote_project_id != null;

  const handleSharedToggle = useCallback(
    (checked: boolean) => {
      const params = new URLSearchParams(searchParams);
      if (checked) {
        params.delete('shared');
      } else {
        params.set('shared', 'off');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCreateTask = () => {
    if (projectId) {
      openTaskForm({ mode: 'create', projectId });
    }
  };

  const handleOpenInIDE = () => {
    handleOpenInEditor();
  };

  const handleOpenOAuth = async () => {
    const profile = await OAuthDialog.show();
    if (profile) {
      await reloadSystem();
    }
  };

  const handleOAuthLogout = async () => {
    try {
      await oauthApi.logout();
      await reloadSystem();
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const handleLocalLogout = async () => {
    try {
      await localAuthApi.logout();
      // reloadSystem refetches user-system → loginStatus becomes null
      // → ProtectedRoute detects !isSignedIn → auto-redirects to /login
      await reloadSystem();
    } catch (err) {
      console.error('Error logging out:', err);
      // Fallback: force redirect if logout API call fails
      window.location.href = '/login';
    }
  };

  const handleBetaWorkspacesToggle = async () => {
    if (!config) return;
    await updateAndSaveConfig({ beta_workspaces: !config.beta_workspaces });
  };

  const isOAuthLoggedIn = loginStatus?.status === 'loggedin';

  // Fetch local user info
  const { data: localUser } = useQuery({
    queryKey: ['localUser'],
    queryFn: localAuthApi.getCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return (
    <div className="border-b bg-background">
      <div className="w-full px-3">
        <div className="flex items-center h-12 py-2">
          <div className="flex-1 flex items-center">
            <Link to="/projects">
              <h1 className="text-xl font-bold">PRIVATE AI</h1>
            </Link>
            {/* <a
              href="https://discord.gg/AC4nwVtJM3"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join our Discord"
              className="hidden sm:inline-flex items-center ml-3 text-xs font-medium overflow-hidden border h-6"
            >
              <span className="bg-muted text-foreground flex items-center p-2 border-r">
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d={siDiscord.path} />
                </svg>
              </span>
              <span
                className=" h-full items-center flex p-2"
                aria-live="polite"
              >
                {onlineCount != null
                  ? `${onlineCount.toLocaleString()} online`
                  : 'online'}
              </span>
            </a> */}
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <SearchBar
              ref={setSearchBarRef}
              className="shrink-0"
              value={query}
              onChange={setQuery}
              disabled={!active}
              onClear={clear}
              project={project || null}
            />
          </div>

          <div className="flex flex-1 items-center justify-end gap-1">
            {isOAuthLoggedIn && shouldShowSharedToggle ? (
              <>
                <div className="flex items-center gap-4">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Switch
                            checked={showSharedTasks}
                            onCheckedChange={handleSharedToggle}
                            aria-label={t('tasks:filters.sharedToggleAria')}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t('tasks:filters.sharedToggleTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <NavDivider />
              </>
            ) : null}

            {projectId ? (
              <>
                <div className="flex items-center gap-1">
                  {isSingleRepoProject && hasPermission('project.workspace.run') && (
                    <>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9"
                              onClick={handleOpenInVscodeWeb}
                              aria-label="Open in VS Code Web"
                            >
                              <Globe className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            Open in VS Code Web
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <OpenInIdeButton
                        onClick={handleOpenInIDE}
                        className="h-9 w-9"
                      />
                    </>
                  )}
                  {hasPermission('project.tasks.create') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={handleCreateTask}
                      aria-label="Create new task"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <NavDivider />
              </>
            ) : null}

            {/* <Button variant="ghost" size="sm" className="h-9 gap-1.5" asChild>
              <Link to="/workspaces">
                <Sparkles className="h-4 w-4" />
                {t('common:navbar.tryNewUI')}
              </Link>
            </Button>
            <NavDivider /> */}

            <div className="flex items-center gap-1">
              {isAdmin && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-9 w-9 ${config?.beta_workspaces ? 'text-brand' : ''}`}
                        onClick={handleBetaWorkspacesToggle}
                        aria-label="Toggle Workspaces Beta"
                      >
                        <FlaskConical className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {config?.beta_workspaces
                        ? 'Workspaces Beta: ON'
                        : 'Enable Workspaces Beta'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {hasPermission('system.settings.view') && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  asChild
                  aria-label="Settings"
                >
                  <Link
                    to={
                      projectId
                        ? `/settings/projects?projectId=${projectId}`
                        : '/settings'
                    }
                  >
                    <Settings className="h-4 w-4" />
                  </Link>
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={
                      isOAuthLoggedIn || localUser
                        ? 'h-9 rounded-full px-3 gap-2'
                        : 'h-9 w-9'
                    }
                    aria-label={
                      isOAuthLoggedIn || localUser
                        ? 'User menu'
                        : 'Main navigation'
                    }
                  >
                    {isOAuthLoggedIn || localUser ? (
                      <>
                        <User className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {localUser?.username ||
                            (loginStatus?.status === 'loggedin'
                              ? loginStatus.profile?.username
                              : null) ||
                            'User'}
                        </span>
                      </>
                    ) : (
                      <Menu className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-56">
                  {INTERNAL_NAV.map((item) => {
                    const active = location.pathname.startsWith(item.to);
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem
                        key={item.to}
                        asChild
                        className={active ? 'bg-accent' : ''}
                      >
                        <Link to={item.to}>
                          <Icon className="mr-2 h-4 w-4" />
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}

                  <DropdownMenuSeparator />

                  {EXTERNAL_LINKS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon className="mr-2 h-4 w-4" />
                          {item.label}
                        </a>
                      </DropdownMenuItem>
                    );
                  })}

                  <DropdownMenuSeparator />

                  {isOAuthLoggedIn ? (
                    <DropdownMenuItem onSelect={handleOAuthLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('common:signOut')}
                    </DropdownMenuItem>
                  ) : localUser ? (
                    <DropdownMenuItem onSelect={handleLocalLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={handleOpenOAuth}>
                      <LogIn className="mr-2 h-4 w-4" />
                      Sign in
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
